<template lang="html">
  <v-content class="pa-0 ma-0">
    <v-dialog
      v-model="deleteSchedulerDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      width="600"
      @keydown.esc="deleteSchedulerDialogOpened = false"
    >
      <v-card block flat>
        <v-card-title class="title"> Confirmation </v-card-title>
        <v-card-text class="text-xs-left subheading">
          <v-alert type="error" :value="true" outline>
            Are you sure you want to remove the scheduled templates? This will
            not remove the templates that have been set out already.
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn class="primary text-none" @click="deleteSelectedScheduler()">
            Yes
          </v-btn>
          <v-btn
            class="error--text text-none"
            flat
            @click="deleteSchedulerDialogOpened = false"
          >
            No
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog
      v-model="openDialog"
      width="800"
      persistent
      @keydown.esc="openDialog = false"
    >
      <v-card block flat>
        <v-card-title class="pb-0">
          <v-layout align-center>
            <span class="title">Scheduled Templates</span>
            <v-spacer />
            <v-switch
              v-model="showMine"
              class="ma-0 pa-0 default-input"
              label="Show Mine Only"
              hide-details
            />
            <v-switch
              v-model="showActive"
              class="ma-0 pa-0 default-input"
              label="Show Active Only"
              hide-details
            />
            <v-btn
              flat
              class="text-none primary--text mr-0 my-0"
              @click="$emit('create-template-scheduler')"
            >
              <v-icon class="mr-1" size="25"> add </v-icon>
            </v-btn>
          </v-layout>
        </v-card-title>
        <v-card-text v-if="schedulerLoading">
          <v-progress-circular
            indeterminate
            size="25"
            color="primary"
          ></v-progress-circular>
        </v-card-text>
        <v-card-text v-else>
          <v-alert
            v-if="isEmpty(templateSchedulers)"
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-2"
          >
            You have no scheduled templates.
          </v-alert>
          <v-alert
            v-else
            :value="true"
            outline
            block
            :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
            color="info"
            class="text-xs-left mb-2"
          >
            We will send the scheduled templates at 3PM for your timezone (You
            can change the timezone in
            <span
              style="cursor: pointer"
              class="primary--text font-weight-bold"
              @click="$router.push('/firm-settings')"
              >Manage Firm</span
            >). You can still cancel the scheduler if canceled before 2:50PM
            (delete or inactivate).
          </v-alert>
          <v-layout
            align-center
            v-for="(scheduler, i) in templateSchedulers"
            :key="i"
            class="outlined pa-2 mb-4"
          >
            <v-layout column>
              <v-layout class="text-xs-left mb-4">
                <v-flex xs4 class="pr-2">
                  <div>Recipients</div>
                  <v-autocomplete
                    multiple
                    class="default-input body-1"
                    hide-details
                    small-chips
                    solo
                    :items="companies"
                    v-model="scheduler.company_ids"
                    placeholder="Select entities"
                    item-text="name"
                    item-value="id"
                    @change="updateScheduler(scheduler)"
                  >
                    <template slot="selection" slot-scope="data">
                      <v-chip
                        small
                        v-if="scheduler.company_ids.indexOf(data.item.id) === 0"
                      >
                        <span>{{
                          findEntityName(scheduler.company_ids[0])
                        }}</span>
                      </v-chip>
                      <span
                        v-if="scheduler.company_ids.indexOf(data.item.id) === 1"
                        class="grey--text caption"
                        >(+{{ scheduler.company_ids.length - 1 }} other{{
                          scheduler.company_ids.length > 2 ? 's' : ''
                        }})</span
                      >
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex xs6 class="px-2">
                  <div>Summary</div>
                  <div class="grey--text text--darken-1">
                    Send
                    <b>{{
                      findTemplateName(scheduler.questionnaire_template_id)
                    }}</b>
                    template{{ scheduler.year ? ` for ${scheduler.year}` : '' }}
                    <b>{{ findFrequencyText(scheduler.frequency) }}</b>
                    {{
                      scheduler.frequency !== 'once' ? 'starting from' : 'on'
                    }}
                    <b>{{
                      moment(scheduler.first_scheduled_date).format('ll')
                    }}</b
                    >, due in <b>{{ scheduler.days_until_due }}</b> days.
                  </div>
                </v-flex>
                <v-flex xs2 class="pl-2">
                  <div>Status</div>
                  <v-switch
                    class="default-input ma-0 pa-0"
                    solo
                    hide-details
                    :label="
                      scheduler.status === 'active' ? 'Active' : 'Inactive'
                    "
                    true-value="active"
                    false-value="inactive"
                    v-model="scheduler.status"
                    @change="updateScheduler(scheduler)"
                  />
                </v-flex>
              </v-layout>
              <v-layout column class="text-xs-left mb-4">
                <div>
                  To be sent in one single email:
                  <b>{{ scheduler.batch_sending ? 'Yes' : 'No' }}</b>
                </div>
              </v-layout>
              <v-layout column class="text-xs-left mb-4">
                <div>Included Sections</div>
                <v-layout wrap align-center>
                  <v-chip
                    small
                    v-for="section in scheduler.sections"
                    :key="section"
                    >{{ section }}</v-chip
                  >
                </v-layout>
              </v-layout>
            </v-layout>
            <v-tooltip bottom>
              <v-btn
                slot="activator"
                icon
                class="default-input error--text text-none mx-2"
                @click="openDeleteSchedulerDialog(scheduler)"
              >
                <i class="material-icons-outlined"> delete </i>
              </v-btn>
              <span>Delete</span>
            </v-tooltip>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-spacer class="hidden-xs-only" />
          <v-btn
            :block="$vuetify.breakpoint.xsOnly"
            outline
            class="default-input error--text text-capitalize"
            @click="openDialog = false"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-content>
</template>
<script>
import moment from 'moment';
import API from '@/api';
import { mapState, mapGetters } from 'vuex';
import isEmpty from 'lodash/isEmpty';
import take from 'lodash/take';
import get from 'lodash/get';
import { templateFrequencyOptions } from '@/constants/variables';

export default {
  name: 'TemplateSchedulerDialog',
  data() {
    return {
      templateFrequencyOptions,
      openDialog: false,
      templateSchedulers: [],
      questionnaireTemplates: [],
      selectedScheduler: null,
      deleteSchedulerDialogOpened: false,
      schedulerLoading: false,
      showActive: true,
      showMine: true,
    };
  },
  props: {
    currentCompanyId: {
      type: Number,
    },
    value: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState('companies', ['companies']),
  },
  methods: {
    findFrequencyText(frequency) {
      const match = this.templateFrequencyOptions.find(
        c => c.value === frequency,
      );
      if (match) {
        return match.text.toLowerCase();
      }
    },
    findTemplateName(id) {
      return this.questionnaireTemplates.find(c => c.id === id).name;
    },
    findEntityName(company_id) {
      return this.companies.find(c => c.id === company_id).name;
    },
    isEmpty,
    moment,
    openDeleteSchedulerDialog(scheduler) {
      this.selectedScheduler = scheduler;
      this.deleteSchedulerDialogOpened = true;
    },
    async deleteSelectedScheduler() {
      this.deleteSchedulerDialogOpened = false;
      const response = await API.scheduler.deleteTemplateScheduler(
        this.selectedScheduler.id,
      );
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem removing the scheduled template.',
        });
      } else {
        this.refreshSchedulers();
        this.$store.dispatch('application/setSuccessNotification', {
          message: "You've successfully removed the scheduled template.",
        });
      }
    },
    async updateScheduler(scheduler) {
      const response = await API.scheduler.updateTemplateScheduler(scheduler);
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem updating the scheduled template.',
        });
      } else {
        this.refreshSchedulers();
      }
    },
    async refreshSchedulers() {
      this.schedulerLoading = true;
      try {
        if (this.currentCompanyId) {
          const response2 = await API.scheduler.getTemplateSchedulersForCompany(
            {
              companyId: this.currentCompanyId,
              showActive: this.showActive,
              showMine: this.showMine,
            },
          );
          if (response2.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: response2.data.error,
            });
          } else {
            this.templateSchedulers = response2.data;
          }
        } else {
          const response1 = await API.scheduler.getAllTemplateSchedulers({
            showActive: this.showActive,
            showMine: this.showMine,
          });
          if (response1.data.error) {
            this.$store.dispatch('application/setErrorNotification', {
              message: response1.data.error,
            });
          } else {
            this.templateSchedulers = response1.data;
          }
        }
      } catch (e) {
        console.log(e);
      } finally {
        this.schedulerLoading = false;
      }
    },
    async getAllQuestionnaireTemplates() {
      const response = await API.questionnaireTemplate.getAllQuestionnaireTemplatesInfo();
      this.questionnaireTemplates = response.data;
    },
  },
  created() {
    this.openDialog = this.value;
  },
  watch: {
    async value(newVal) {
      this.openDialog = newVal;
      if (this.openDialog) {
        await this.getAllQuestionnaireTemplates();
        this.refreshSchedulers();
      }
    },
    openDialog(newVal) {
      this.$emit('input', newVal);
    },
    showActive(newVal) {
      this.refreshSchedulers();
    },
    showMine(newVal) {
      this.refreshSchedulers();
    },
  },
};
</script>

<style lang="postcss" scoped></style>
